import * as React from "react";
import { ArrowRight } from "lucide-react";

import { cn } from "../../lib/utils.js";

/**
 * Success / confirmation card. `children` are optional extra content rendered
 * between the description and the actions. On hover (or keyboard focus, or `active`) a
 * glow grows from the lower edge until it washes the card in a mild tint of
 * the tone's color; leaving reverses it. The wash is deliberately faint, so
 * the text keeps its resting colors rather than flipping to white.
 *
 * Every visual value lives in `config` (see confirmationCardDefaults) and is
 * mapped to --cc-* CSS custom properties on the root, which the
 * .confirm-card rules in styles/tokens.css consume -- change a number here,
 * never the CSS. All durations derive from `animationDuration` unless set
 * explicitly, so one number speeds the whole animation up or down.
 */
export interface ConfirmationCardConfig {
  // ── Size & spacing (px) ────────────────────────────────────────────────
  /** Max width; the card never exceeds its container (it shrinks on small screens). */
  width?: number;
  minHeight?: number;
  /** Upper bound; padding scales down on narrow screens. */
  padding?: number;
  borderRadius?: number;

  // ── Typography (px, weights as numbers) ─────────────────────────────────
  titleFontSize?: number;
  titleWeight?: number;
  titleMarginTop?: number;
  titleMarginBottom?: number;
  descriptionFontSize?: number;
  descriptionLineHeight?: number;
  buttonFontSize?: number;
  buttonWeight?: number;
  /** Space above the actions row. */
  buttonMarginTop?: number;
  /** Gap between the action text and its arrow. */
  buttonGap?: number;
  /** Gap between the primary action and the secondary one (`cancelText`). */
  actionsGap?: number;
  /** Padding of the hover pill that surrounds each action's label. */
  actionPaddingX?: number;
  actionPaddingY?: number;
  actionRadius?: number;

  // ── Colors (any CSS color, including var(--color-…) tokens) ─────────────
  backgroundColor?: string;
  titleColor?: string;
  descriptionColor?: string;
  buttonColor?: string;
  /** The secondary action (`cancelText`), which reads quieter than the primary. */
  cancelColor?: string;
  /** Hover: the primary action reads as "go", the secondary as "stop". Each
   * also gets a translucent pill of its own color behind the label. */
  actionHoverColor?: string;
  cancelHoverColor?: string;
  /** The resting orb glowing at the lower edge. */
  accentColor?: string;
  /** Colors once the wash has covered the card. */
  hoverBackgroundColor?: string;
  hoverTextColor?: string;
  shadowColor?: string;
  shadowBlur?: number;
  shadowOffsetY?: number;

  // ── Glow ────────────────────────────────────────────────────────────────
  /**
   * Diameter the glow grows to on hover, px -- or "cover" (default) to always
   * flood the whole card whatever its size, content or padding.
   */
  glowSize?: number | "cover";
  /** Diameter of the resting orb at the lower edge, px. */
  glowRestSize?: number;
  /** Softness of the resting orb, px. */
  glowBlur?: number;
  glowOpacity?: number;
  /** Horizontal glow origin, % of card width (50 = centered). */
  glowPositionX?: number;
  /** Glow origin relative to the card's bottom edge, px (positive = below the edge). */
  glowOffsetY?: number;

  // ── Motion ──────────────────────────────────────────────────────────────
  /** ms for the glow to flood the card; every other duration derives from it. */
  animationDuration?: number;
  animationEasing?: string;
  /** Explicit ms overrides; omitted -> derived from animationDuration. */
  textTransitionDuration?: number;
  backgroundTransitionDuration?: number;
  cardTransitionDuration?: number;
  hoverScale?: number;
  /** px (negative lifts the card). */
  hoverTranslateY?: number;
  /** false: no hover animation at all -- a static card. */
  enableHoverAnimation?: boolean;
}

export interface ConfirmationCardContent {
  title: React.ReactNode;
  description?: React.ReactNode;
  actionText?: React.ReactNode;
  /** Secondary action, rendered beside the primary one inside the card (so the
   * wash covers both). Omit it for a single-action card. */
  cancelText?: React.ReactNode;
}

/** Every default the card falls back to -- pass only what you want to change. */
export const confirmationCardDefaults = {
  width: 340,
  minHeight: 300,
  padding: 28,
  borderRadius: 30,

  titleFontSize: 26,
  titleWeight: 500,
  titleMarginTop: 16,
  titleMarginBottom: 16,
  descriptionFontSize: 14,
  descriptionLineHeight: 22,
  buttonFontSize: 14,
  buttonWeight: 600,
  buttonMarginTop: 24,
  buttonGap: 12,
  actionsGap: 24,
  actionPaddingX: 12,
  actionPaddingY: 7,
  actionRadius: 8,

  // Theme tokens, so the card follows light/dark mode out of the box.
  backgroundColor: "var(--color-surface-0)",
  titleColor: "var(--color-surface-900)",
  descriptionColor: "var(--color-surface-500)",
  buttonColor: "var(--color-success-fg)",
  cancelColor: "var(--color-surface-500)",
  actionHoverColor: "var(--color-success)",
  cancelHoverColor: "var(--color-error)",
  // Mixed against the card's own surface, so both read correctly in either
  // theme: a faint tint on hover, a slightly stronger orb at rest.
  accentColor: "color-mix(in srgb, var(--color-success) 35%, var(--color-surface-0))",
  hoverBackgroundColor: "color-mix(in srgb, var(--color-success) 14%, var(--color-surface-0))",
  // The wash is too faint to carry white text -- the title keeps its color.
  hoverTextColor: "var(--color-surface-900)",
  shadowColor: "rgba(192, 192, 192, 0.2)",
  shadowBlur: 14,
  shadowOffsetY: 4,

  glowSize: "cover",
  glowRestSize: 180,
  glowBlur: 40,
  glowOpacity: 1,
  glowPositionX: 50,
  glowOffsetY: 40,

  animationDuration: 1000,
  animationEasing: "ease",
  hoverScale: 1,
  hoverTranslateY: 0,
  enableHoverAnimation: true,
} as const satisfies Required<Omit<ConfirmationCardConfig,
  "textTransitionDuration" | "backgroundTransitionDuration" | "cardTransitionDuration">>;

type CssVars = Record<`--cc-${string}`, string>;

const px = (n: number) => `${n}px`;
const ms = (n: number) => `${n}ms`;

/**
 * The single config -> CSS mapping (CLAUDE.md "typed config object -> CSS
 * custom properties" exception). Derived durations are left to the CSS
 * fallbacks, so they track animationDuration automatically.
 */
function toCssVars(config: ConfirmationCardConfig): CssVars {
  const c = { ...confirmationCardDefaults, ...config };
  const vars: CssVars = {
    "--cc-width": px(c.width),
    "--cc-min-height": px(c.minHeight),
    "--cc-padding": px(c.padding),
    "--cc-radius": px(c.borderRadius),
    "--cc-title-size": px(c.titleFontSize),
    "--cc-title-weight": String(c.titleWeight),
    "--cc-title-mt": px(c.titleMarginTop),
    "--cc-title-mb": px(c.titleMarginBottom),
    "--cc-desc-size": px(c.descriptionFontSize),
    "--cc-desc-leading": px(c.descriptionLineHeight),
    "--cc-button-size": px(c.buttonFontSize),
    "--cc-button-weight": String(c.buttonWeight),
    "--cc-button-mt": px(c.buttonMarginTop),
    "--cc-button-gap": px(c.buttonGap),
    "--cc-actions-gap": px(c.actionsGap),
    "--cc-action-pad-x": px(c.actionPaddingX),
    "--cc-action-pad-y": px(c.actionPaddingY),
    "--cc-action-radius": px(c.actionRadius),
    "--cc-bg": c.backgroundColor,
    "--cc-title-color": c.titleColor,
    "--cc-desc-color": c.descriptionColor,
    "--cc-button-color": c.buttonColor,
    "--cc-cancel-color": c.cancelColor,
    "--cc-action-hover-color": c.actionHoverColor,
    "--cc-cancel-hover-color": c.cancelHoverColor,
    "--cc-accent": c.accentColor,
    "--cc-hover-bg": c.hoverBackgroundColor,
    "--cc-hover-text": c.hoverTextColor,
    "--cc-shadow-color": c.shadowColor,
    "--cc-shadow-blur": px(c.shadowBlur),
    "--cc-shadow-y": px(c.shadowOffsetY),
    // clip-path circle(): 200% of the box's reference radius always reaches
    // every corner, whatever the card's width, height or content.
    "--cc-glow-radius": c.glowSize === "cover" ? "200%" : px(c.glowSize / 2),
    "--cc-glow-rest-size": px(c.glowRestSize),
    "--cc-glow-blur": px(c.glowBlur),
    "--cc-glow-opacity": String(c.glowOpacity),
    "--cc-glow-x": `${c.glowPositionX}%`,
    "--cc-glow-offset-y": px(c.glowOffsetY),
    "--cc-duration": ms(c.animationDuration),
    "--cc-easing": c.animationEasing,
    "--cc-hover-scale": String(c.hoverScale),
    "--cc-hover-y": px(c.hoverTranslateY),
  };
  if (config.textTransitionDuration != null) vars["--cc-text-duration"] = ms(config.textTransitionDuration);
  if (config.backgroundTransitionDuration != null) vars["--cc-bg-duration"] = ms(config.backgroundTransitionDuration);
  if (config.cardTransitionDuration != null) vars["--cc-card-duration"] = ms(config.cardTransitionDuration);
  return vars;
}

export interface ConfirmationCardProps extends Omit<React.HTMLAttributes<HTMLElement>, "content" | "style"> {
  config?: ConfirmationCardConfig;
  content: ConfirmationCardContent;
  /** Action as a button. */
  onAction?: () => void;
  /** Blocks the primary action until the card's own content says it's allowed
   * (e.g. an attestation checkbox passed as `children`). */
  actionDisabled?: boolean;
  /** Action as a link (takes precedence over onAction for the element type). */
  actionHref?: string;
  /** Fires from the secondary action; only rendered when `content.cancelText` is set. */
  onCancel?: () => void;
  /** Force the hovered/"flooded" look, e.g. to celebrate right after a submit, or on touch screens. */
  active?: boolean;
  /** Heading level for the title. */
  titleAs?: "h2" | "h3" | "h4";
}

const ConfirmationCard = React.forwardRef<HTMLElement, ConfirmationCardProps>(
  ({ config = {}, content, onAction, actionDisabled, actionHref, onCancel, active, titleAs: Title = "h3", className, children, onPointerEnter, onPointerLeave, ...props }, ref) => {
    const titleId = React.useId();
    const animated = config.enableHoverAnimation ?? confirmationCardDefaults.enableHoverAnimation;
    const vars = React.useMemo(() => toCssVars(config), [config]);

    const actionInner = (
      <>
        {content.actionText}
        <ArrowRight aria-hidden="true" className="confirm-card__action-arrow" />
      </>
    );

    const action = content.actionText && (actionHref ? (
      <a href={actionHref} className="confirm-card__action">{actionInner}</a>
    ) : (
      <button type="button" onClick={onAction} disabled={actionDisabled} className="confirm-card__action">{actionInner}</button>
    ));

    return (
      <section
        ref={ref}
        aria-labelledby={titleId}
        data-animated={animated ? "" : undefined}
        data-active={active ? "" : undefined}
        // Mouse/pen hover only -- a touch tap would otherwise leave :hover stuck
        // on. Set on the DOM directly so hovering never re-renders.
        onPointerEnter={(e) => { onPointerEnter?.(e); if (e.pointerType !== "touch") e.currentTarget.dataset.hover = ""; }}
        onPointerLeave={(e) => { onPointerLeave?.(e); delete e.currentTarget.dataset.hover; }}
        className={cn("confirm-card", className)}
        style={vars as React.CSSProperties}
        {...props}
      >
        <span aria-hidden="true" className="confirm-card__orb" />
        <Title id={titleId} className="confirm-card__title">{content.title}</Title>
        {content.description && <p className="confirm-card__description">{content.description}</p>}
        {/* Anything the decision needs before it can be made -- a preview to
            look at, an attestation to tick. Sits between the description and
            the actions, inside the wash like the rest of the content. */}
        {children && <div className="confirm-card__body">{children}</div>}
        {content.cancelText ? (
          <div className="confirm-card__actions">
            {action}
            <button type="button" onClick={onCancel} className="confirm-card__cancel">{content.cancelText}</button>
          </div>
        ) : action}
      </section>
    );
  }
);
ConfirmationCard.displayName = "ConfirmationCard";

export { ConfirmationCard };
